import React, { FC, useEffect, useState, useMemo, useCallback } from 'react'
import { Stripe, loadStripe } from '@stripe/stripe-js'
import { useSelector, useDispatch } from 'react-redux'

import snackbarActions from '@kaidam/fc-shared/src/frontend/store/modules/snackbar'
import routerActions from '@kaidam/fc-shared/src/frontend/store/modules/router'
import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'

import {
  IData_Org_With_Profile,
} from '@kaidam/fc-shared/src/types/rbac'

import {
  IData_Booking_Pricing_Product,
  IData_Booking_Settings_Org,
  IData_Booking_General_Config,
  IData_Calendar_Public_Day,
  IData_Calendar_Public_Day_Merged,
  IData_Booking,
  IData_Booking_Payment_Request,
  IData_Booking_Payment_Response,
  IData_Booking_Confirm_Request,
  IData_Booking_Confirm_Response,
  IRecord_Booking_Core_Viewable,
} from '@kaidam/fc-shared/src/types/schema/booking'

import {
  ILayout,
  ILayout_Render_Context,
} from '@kaidam/fc-shared/src/types/layout'

import {
  handlers,
} from '@kaidam/fc-shared/src/frontend/store/utils/api'

import {
  getDateString,
  getDateDaysFromToday,
  isDateBeforeBuffer,
} from '@kaidam/fc-shared/src/booking/utils'

import {
  mergePublicCalendar,
} from '@kaidam/fc-shared/src/booking/model'

import exportHTMLLayout from '@kaidam/fc-shared/src/layout/html'

import {
  renderMagicString,
} from '@kaidam/fc-shared/src/texteditor/utils'

import {
  DEFAULT_GENERAL_CONFIG,
  DEFAULT_SETTINGS,
} from './defaultValues'

export type IBookingPageName = 'product' | 'calendar' | 'details' | 'payment' | 'payment_confirm'

const PAGE_SCORES: Record<IBookingPageName, number> = {
  product: 0,
  calendar: 1,
  details: 2,
  payment: 3,
  payment_confirm: 4,
}

const PAGES: IBookingPageName[] = [
  'product',
  'calendar',
  'details',
  'payment',
  'payment_confirm',
]

export interface IBookingContext {
  initialised: boolean,
  postcodeInitialised: boolean,
  loading: boolean,
  orgs: IData_Org_With_Profile[],
  org?: IData_Org_With_Profile,
  postcode: string,
  coverageETs: string[],
  products: IData_Booking_Pricing_Product[],
  calendarDate: string,
  earliestBookingDate: string,
  latestBookingDate: string,
  calendar: IData_Calendar_Public_Day_Merged[],
  settings: IData_Booking_Settings_Org,
  generalConfig: IData_Booking_General_Config,
  data: IData_Booking,
  confirmedBooking?: IRecord_Booking_Core_Viewable,
  pageIndex: number,
  pageScore: number,
  pageName: IBookingPageName,
  confirmError: string,
  setBookingPostcode: {
    (postcode: string): void,
  },
  setCalendarDate: {
    (date: string): void,
  },
  update: {
    (data: Partial<IData_Booking>): void,
  },
  openPage: {
    (page: IBookingPageName | number): void,
  },
  restart: {
    (): void,
  },
  pay: {
    (): void,
  },
  confirmPayment: {
    (): void,
  },
  clearConfirmPayment: {
    (): void,
  },
  renderLayout: {
    (layout: ILayout): string,
  }
}

export const defaultBookingContext: IBookingContext = {
  initialised: false,
  postcodeInitialised: false,
  loading: false,
  orgs: [],
  org: null,
  coverageETs: [],
  postcode: '',
  products: [],
  calendarDate: getDateString(new Date()),
  earliestBookingDate: getDateString(new Date()),
  latestBookingDate: getDateString(new Date()),
  calendar: [],
  data: {},
  confirmedBooking: undefined,
  pageIndex: 0,
  pageScore: 0,
  pageName: 'product',
  confirmError: '',
  setBookingPostcode: () => {},
  update: () => { },
  openPage: () => { },
  setCalendarDate: () => { },
  restart: () => { },
  pay: () => { },
  confirmPayment: () => { },
  clearConfirmPayment: () => { },
  renderLayout: () => '',
  settings: DEFAULT_SETTINGS,
  generalConfig: DEFAULT_GENERAL_CONFIG,

}

const useBooking = ({

} = {}): IBookingContext => {

  const dispatch = useDispatch()
  const params = useSelector(routerSelectors.params)
  const route = useSelector(routerSelectors.route)

  const [initialised, setInitialised] = useState(false)
  const [postcodeInitialised, setPostcodeInitialised] = useState(false)
  const [loading, setLoading] = useState(false)
  const [postcode, setPostcode] = useState<string>('')
  const [coverageETs, setCoverageETs] = useState<string[]>([])
  const [orgs, setOrgs] = useState<IData_Org_With_Profile[]>([])
  const [settings, setSettings] = useState<IData_Booking_Settings_Org>(undefined)
  const [savedBooking, setSavedBooking] = useState<IData_Booking>({})
  const [calendar, setCalendar] = useState<IData_Calendar_Public_Day_Merged[]>([])
  const [calendarDate, setCalendarDate] = useState<string>('')
  const [earliestBookingDate, setEarliestBookingDate] = useState<string>('')
  const [latestBookingDate, setLatestBookingDate] = useState<string>('')
  const [confirmedBooking, setConfirmedBooking] = useState<IRecord_Booking_Core_Viewable>(undefined)
  const [confirmError, setConfrmError] = useState('')

  /*
  
    MEMO
  
  */
  const org = useMemo(() => {
    return orgs.find(o => {
      if(o.id == params.orgid) return true
      if(o.profile.website_url == params.orgid) return true
      return false
    })
  }, [
    orgs,
    params,
  ])

  const products = useMemo<IData_Booking_Pricing_Product[]>(() => {
    if (!settings) return []
    return settings.settings.pricing.products
  }, [
    settings,
  ])

  const routePageName = useMemo<IBookingPageName>(() => {
    return route.name.split('.')[1] as IBookingPageName
  }, [
    route.name,
  ])

  const bookingPageScore = useMemo(() => {
    if (!savedBooking.product) return 0
    if (!savedBooking.date) return 1
    if (!savedBooking.details) return 2
    return 3
  }, [
    savedBooking,
  ])

  const generalConfig = useMemo(() => {
    const config: IData_Booking_General_Config = settings?.settings?.general_config || DEFAULT_GENERAL_CONFIG
    return config
  }, [
    settings,
  ])

  const pageIndex = PAGE_SCORES[routePageName]


  /*
  
    CALLBACK
  
  */
  const update = useCallback((data: Partial<IData_Booking>) => {
    const newBooking = Object.assign({}, savedBooking, data)
    setSavedBooking(newBooking)
    localStorage.setItem('booking', JSON.stringify(newBooking))
  }, [
    savedBooking,
  ])

  const openPage = useCallback((page: IBookingPageName | number) => {
    if (typeof page === 'number') {
      page = PAGES[pageIndex + page]
    }
    dispatch(routerActions.navigateTo(`booking.${page}`, {
      orgid: org.id,
    }))
  }, [
    pageIndex,
    org,
  ])

  // initialise the booking state with the gievn postcode
  // this means we know how many ETs there are
  const setBookingPostcode = useCallback(async (postcode: string) => {
    const data = await handlers.get<{
      org: string,
      ets: string[],
    }>(`/ft_booking_public/match_postcode`, {
      params: {
        postcode,
      },
    })
    if(!data.org || data.org != params.orgid) {
      dispatch(snackbarActions.setError(`postcode org does not match`))
      dispatch(routerActions.navigateTo('home'))
      return
    }
    setCoverageETs(data.ets)
    setPostcode(postcode)
    setPostcodeInitialised(true)
    localStorage.setItem('postcode', postcode)
  }, [])

  const restart = useCallback(() => {
    dispatch(routerActions.navigateTo('home'))
  }, [
    params,
    openPage,
    savedBooking,
  ])

  const pay = useCallback(async () => {
    setLoading(true)
    try {
      if (!org) throw new Error('No orgid')
      const orgid = org.id
      const payload: IData_Booking_Payment_Request = {
        data: savedBooking as Required<IData_Booking>,
        org: orgid,
        cancel_url: document.location.href,
        success_url: `${document.location.protocol}//${document.location.host}/booking/${orgid}/payment_confirm`,
      }
      const data = await handlers.post<IData_Booking_Payment_Request, IData_Booking_Payment_Response>(`/ft_booking_public/booking_payment`, payload)
      localStorage.setItem('booking_payment_id', data.session.id)
      const stripe = await loadStripe(data.public_key)
      if (!stripe) throw new Error(`stripe client could not be created`)
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.session.id,
      })
      if (error) throw error
    } catch (e) {
      console.error(e)
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
      setLoading(false)
    }
  }, [
    savedBooking,
    org,
  ])

  const confirmPayment = useCallback(async () => {
    setLoading(true)
    try {
      if (!org) throw new Error('No orgid')
      const orgid = org.id
      if (!savedBooking) throw new Error('No booking')
      if (!savedBooking.date) throw new Error('No date')
      if (!savedBooking.product) throw new Error('No product')
      if (!savedBooking.details) throw new Error('No details')
      const session_id = localStorage.getItem('booking_payment_id')
      if (!session_id) throw new Error('No stripe session id found')
      const result = await handlers.post<IData_Booking_Confirm_Request, IData_Booking_Confirm_Response>(`/ft_booking_public/booking_confirm`, {
        data: savedBooking as Required<IData_Booking>,
        org: orgid,
        session_id,
      })
      if (!result.confirmed) throw new Error(`there was a problem with the payment`)
      if (!result.booking) throw new Error(`there was no booking returned`)
      setConfirmedBooking(result.booking)
      dispatch(snackbarActions.setSuccess(`your booking has been confirmed`))
    } catch (e) {
      console.error(e)
      const errorString = e.response ? e.response.data.error : e.toString()
      setConfrmError(errorString)
      dispatch(snackbarActions.setError(errorString))
    }
    setLoading(false)
  }, [
    savedBooking,
    org,
  ])

  const clearConfirmPayment = useCallback(async () => {
    dispatch(routerActions.navigateTo(`login`, {
      reference: confirmedBooking.data.reference,
      emailpostcode: confirmedBooking.data.crm.email,
    }))
    setSavedBooking({})
    setConfirmedBooking(undefined)
    localStorage.removeItem('postcode')
    localStorage.removeItem('booking')
    localStorage.removeItem('booking_payment_id')
  }, [
    confirmedBooking,
  ])

  const magicTags = useMemo(() => {
    if(!org || !org.profile) {
      return {
        org: {},
      }
    }
    const profile = Object.assign({}, org.profile)
    const meta = profile.meta || {}
    delete(profile.meta)
    const values = Object.assign({}, meta, profile, {
      id: org.id,
    })
    return {
      org: values,
    }
  }, [
    org,
  ])

  const renderLayout = useCallback((layout: ILayout): string => {
    const context: ILayout_Render_Context = {
      project: settings.project || '',
      disableLinks: false,
      values: magicTags,
    }
    const template = exportHTMLLayout(layout, context)
    return renderMagicString(template, magicTags)
  }, [
    magicTags,
    settings,
  ])

  /*
  
    EFFECT
  
  */
  // load the org settings
  useEffect(() => {
    if (!org) return
    const doAsync = async () => {
      const data = await handlers.get<IData_Booking_Settings_Org>(`/ft_booking_public/org_settings/${org.id}`)
      setSettings(data)
    }
    doAsync()
  }, [
    org,
  ])

  // load the list of orgs
  useEffect(() => {
    const doAsync = async () => {
      const data = await handlers.get<IData_Org_With_Profile[]>(`/ft_booking_public/orgs`)
      setOrgs(data)
    }
    doAsync()
  }, [])

  // load the public calendar
  useEffect(() => {
    if (!org) return
    if (routePageName != 'calendar') return
    if (!savedBooking.product?.length) return
    if (!calendarDate) return
    if (!postcode) return
    const doAsync = async () => {
      try {
        const data = await handlers.get<IData_Calendar_Public_Day[]>(`/ft_booking_public/calendar/${org.id}`, {
          params: {
            date: calendarDate,
            days: 3,
            length: savedBooking.product.length,
            postcode,
          },
        })
        setCalendar(mergePublicCalendar(data))
      } catch(e) {
        setCalendar([])
        throw e
      }
    }
    doAsync()
  }, [
    org,
    routePageName,
    calendarDate,
    savedBooking,
    postcode,
  ])

  // check that we are not on a page we don't the data for
  useEffect(() => {
    if (!initialised) return
    if (routePageName == 'payment_confirm') return
    if (bookingPageScore < PAGE_SCORES[routePageName]) openPage(PAGES[bookingPageScore])
  }, [
    initialised,
    routePageName,
    bookingPageScore,
  ])

  // initialise the booking data with what is in local storage
  useEffect(() => {
    if(!settings) return
    const savedString = localStorage.getItem('booking')
    const savedPostcode = localStorage.getItem('postcode')
    let useCalendarDate = ''
    const bufferZone = settings.settings.general_config?.buffer_zone || 0
    const cutoffZone = settings.settings.general_config?.cutoff_zone || 0
    const earliestBookingDate = getDateString(getDateDaysFromToday(bufferZone))
    const latestBookingDate = getDateString(getDateDaysFromToday(cutoffZone))
    if (savedString) {
      const bookingData = JSON.parse(savedString) as IData_Booking
      setSavedBooking(bookingData)
      if (bookingData.date) {
        useCalendarDate = getDateString(new Date(bookingData.date))
        if(isDateBeforeBuffer(useCalendarDate, bufferZone)) {
          useCalendarDate = earliestBookingDate
        }
      }
    }
    if(!useCalendarDate) {
      useCalendarDate = earliestBookingDate
    }
    if(savedPostcode) {
      setBookingPostcode(savedPostcode)
    }
    setCalendarDate(useCalendarDate)
    setEarliestBookingDate(earliestBookingDate)
    setLatestBookingDate(latestBookingDate)

    setInitialised(true)
  }, [
    settings,
  ])

  return {
    initialised,
    postcodeInitialised,
    loading,
    orgs,
    org,
    postcode,
    coverageETs,
    pageIndex: PAGE_SCORES[routePageName],
    pageScore: bookingPageScore,
    pageName: routePageName,
    products,
    calendar,
    calendarDate,
    earliestBookingDate,
    latestBookingDate,
    setCalendarDate,
    data: savedBooking,
    confirmedBooking,
    confirmError,
    settings,
    generalConfig,
    update,
    pay,
    restart,
    confirmPayment,
    clearConfirmPayment,
    openPage,
    renderLayout,
    setBookingPostcode,
  }
}

export default useBooking
